import { pick, reject } from 'lodash/fp';
import moment, { ISO_8601, now } from 'moment';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import LoadableState from 'components/shared/loadable';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import { setQueryString } from 'utils/url';
import { hasFeatureFlags } from 'utils/features';
import { AssociatedContent } from '../../AssociatedContent/types';
import * as types from '../types';
import Attendances from './Attendances';
import Unregistered from './Unregistered';

const isPast = date => moment(date, ISO_8601).isBefore(now());

const PAGINATION_DEFAULTS = {
  page: 1,
  totalPages: 0,
  perPage: 10,
  searchCount: 0,
};

class SearchResults extends Component {
  state = {
    loading: true,
    attendances: { data: [], ...PAGINATION_DEFAULTS },
    unregistered: { data: [], ...PAGINATION_DEFAULTS },
    attendanceErrors: {},
  };

  componentDidMount() {
    this.mounted = true;
    this.getSearchResults();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.query !== this.props.query) {
      this.getSearchResults();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  getRouteForResource(resourceType, params = {}) {
    const { event } = this.props;
    const urls = {
      attendances: routes.IN_PERSON_TRAINING.EVENT_ATTENDANCES(event.id),
      unregistered: routes.IN_PERSON_TRAINING.EVENT_UNREGISTERED(event.id),
    };
    return setQueryString(urls[resourceType], params);
  }

  getSearchResults() {
    const { event, query } = this.props;
    this.setState({ loading: true });
    const params = {
      query,
      ...PAGINATION_DEFAULTS,
    };
    const attendancesRoute = this.getRouteForResource('attendances', params);
    const unregisteredRoute = this.getRouteForResource('unregistered', params);

    return Promise.all([
      request.get(attendancesRoute),
      !event.is_archived ? request.get(unregisteredRoute) : Promise.resolve(),
    ]).then(([attendances, unregistered]) => {
      if (this.mounted) {
        this.setState({
          unregistered,
          attendances,
          loading: false,
        });
      }
    });
  }

  updateAttendance = () => {
    const { query } = this.props;
    const resourceRoute = this.getRouteForResource('attendances', {
      query,
      ...PAGINATION_DEFAULTS,
      page: this.state.attendances.page,
    });
    request.get(resourceRoute).then(res => {
      if (this.mounted) {
        this.setState({
          loading: false,
          attendances: {
            ...pick(['page', 'searchCount', 'totalPages', 'perPage'], res),
            data: res.data,
          },
        });
      }
    });
  };

  getNextPage(resourceType) {
    const { query } = this.props;
    const resourceRoute = this.getRouteForResource(resourceType, {
      query,
      ...PAGINATION_DEFAULTS,
      page: this.state[resourceType].page + 1,
    });
    request.get(resourceRoute).then(res => {
      if (this.mounted) {
        this.setState(state => ({
          loading: false,
          [resourceType]: {
            ...pick(['page', 'searchCount', 'totalPages', 'perPage'], res),
            data: state[resourceType].data.concat(res.data),
          },
        }));
      }
    });
  }

  addGuestAttendance = (userId, assignContent = false) => {
    const { event } = this.props;
    if (event.number_of_sessions === 0) return;
    // For a one session event, the next session will be set for an event in the future
    // and the last session will be set for an event in the past.
    // For multi-session events, this will default to adding the user to the nearest session,
    // starting with the next one if set and the closest last session if not.
    const session = event.next_session || event.last_session;
    const attendanceUrl = hasFeatureFlags(['events_in_development'])
      ? routes.IN_PERSON_TRAINING.EVENT_ATTENDANCES(event.id)
      : routes.IN_PERSON_TRAINING.SESSION_ATTENDANCES(session.id);
    request
      .postJSON(attendanceUrl, {
        type: 'User',
        id: userId,
        status: isPast(session.start_time) ? 'did_attend' : null,
        assign_associated_content: assignContent,
      })
      .then(data => {
        if (data.errors.length === 0) {
          this.setState(state => ({
            attendances: {
              ...state.attendances,
              searchCount: state.attendances.searchCount + 1,
              data: state.attendances.data.concat(data.created_attendances),
            },
            unregistered: {
              ...state.unregistered,
              data: reject({ id: userId }, state.unregistered.data),
            },
            attendanceErrors: {},
          }));
        } else {
          this.setState(state => ({
            attendanceErrors: {
              ...state.attendanceErrors,
              [userId]: data.errors,
            },
          }));
        }
      });
  };

  render() {
    const { loading, unregistered, attendances, attendanceErrors } = this.state;
    const showUnregistered =
      !this.props.event.is_archived &&
      attendances.searchCount < PAGINATION_DEFAULTS.perPage;
    return (
      <LoadableState loading={loading}>
        <Attendances
          searchResultsFlag="true"
          sessions={this.props.sessions}
          isMultiSession={this.props.isMultiSession}
          attendances={attendances}
          query={this.props.query}
          onShowMore={() => this.getNextPage('attendances')}
          loading={loading}
          updateAttendance={this.updateAttendance}
        />
        {showUnregistered && (
          <Unregistered
            unregistered={unregistered}
            query={this.props.query}
            onShowMore={() => this.getNextPage('unregistered')}
            loading={loading}
            addGuest={this.addGuestAttendance}
            attendanceErrors={attendanceErrors}
            event={this.props.event}
            associatedContent={this.props.associatedContent}
          />
        )}
      </LoadableState>
    );
  }
}

const mapStateToProps = state => ({
  event: state.event,
  associatedContent: state.associatedContent,
});

SearchResults.propTypes = {
  event: types.Event.isRequired,
  associatedContent: AssociatedContent.isRequired,
};

export default connect(mapStateToProps)(SearchResults);
